import { mdy, csvText, tsvText } from './shared';
import { hmm, decimalHours, splitOvertime, payCents, wageSuspicious } from '../timesheetMath';

// Timesheet exports. Input is the /accounting/timesheets payload flattened to
// one row per shift (`flattenShifts`) or one per employee (`employeeRows`),
// plus the store's accounting-profile payroll settings.

// The profile's payroll-name map wins; the POS name is used when the
// snapshot could resolve it unambiguously; otherwise the employee number.
export const fullName = (e) => `${e.firstName || ''} ${e.lastName || ''}`.trim() || `#${e.employeeId}`;
export const payrollName = (e, qbNames) => (qbNames && qbNames[e.employeeId]) || fullName(e);

export const flattenShifts = (data) => (data?.stores || []).flatMap(s =>
  (s.employees || []).flatMap(e => (e.shifts || []).map(sh => ({ ...sh, employee: e, store: s.name, locationId: s.locationId }))));

export const employeeRows = (data) => (data?.stores || []).flatMap(s =>
  (s.employees || []).map(e => ({ employee: e, store: s.name, locationId: s.locationId })));

// ── Generic CSV ──────────────────────────────────────────────────────────────
export const SHIFT_HEADER = ['Employee ID', 'First name', 'Last name', 'Store', 'Date', 'Clock in', 'Clock out', 'Hours (h:mm)', 'Hours (decimal)', 'Flag'];

export function buildTimesheetCsv(shifts) {
  const rows = [SHIFT_HEADER];
  for (const s of shifts) {
    rows.push([
      s.employee.employeeId, s.employee.firstName, s.employee.lastName, s.store, s.date,
      s.start.slice(11, 16), s.end ? s.end.slice(11, 16) : '',
      hmm(s.minutes), decimalHours(s.minutes).toFixed(2), s.flag || '',
    ]);
  }
  return csvText(rows);
}

export function buildTimesheetTotalsCsv(employees, { overtimeWeeklyHours = null, showPay = false } = {}) {
  const header = ['Employee ID', 'First name', 'Last name', 'Store', 'Shifts', 'Hours (h:mm)', 'Hours (decimal)', 'Regular hours', 'Overtime hours', 'Flagged shifts'];
  if (showPay) header.push('Hourly wage', 'Pay earned');
  const rows = [header];
  for (const { employee: e, store } of employees) {
    const { reg, ot } = splitOvertime(e.shifts, overtimeWeeklyHours);
    const row = [
      e.employeeId, e.firstName, e.lastName, store, e.shifts.length,
      hmm(e.minutes), decimalHours(e.minutes).toFixed(2), decimalHours(reg).toFixed(2), decimalHours(ot).toFixed(2),
      e.shifts.filter(s => s.flag).length,
    ];
    if (showPay) {
      const ok = e.hourlyWage !== null && e.hourlyWage !== undefined && !wageSuspicious(e.hourlyWage);
      row.push(ok ? (e.hourlyWage / 100).toFixed(2) : '', ok ? (payCents(e.minutes, e.hourlyWage) / 100).toFixed(2) : '');
    }
    rows.push(row);
  }
  return csvText(rows);
}

// ── QuickBooks Desktop: IIF TIMEACT ──────────────────────────────────────────
// One timer activity per closed shift. EMP must match the Desktop employee
// name exactly; DURATION is H:MM; XFERTOPAYROLL=Y sends it to payroll.
export const TIMEACT_HEADER = ['!TIMEACT', 'DATE', 'JOB', 'EMP', 'ITEM', 'PITEM', 'DURATION', 'PROJ', 'NOTE', 'XFERTOPAYROLL', 'BILLINGSTATUS'];

export function buildTimeactIif(shifts, { qbNames = {} } = {}) {
  const rows = [TIMEACT_HEADER];
  let skipped = 0;
  for (const s of shifts) {
    if (s.open || !s.minutes) { skipped += 1; continue; }
    rows.push(['TIMEACT', mdy(s.date), '', payrollName(s.employee, qbNames), '', '', hmm(s.minutes), '',
      s.flag ? `${s.store} — ${s.flag}` : s.store, 'Y', '0']);
  }
  return { text: tsvText(rows), count: rows.length - 1, skipped };
}

// ── QuickBooks Online: timesheet CSV ─────────────────────────────────────────
export const QBO_TIME_HEADER = ['TxnDate', 'Name', 'TimeStart', 'TimeEnd', 'TimeDescription', 'Billable', 'Status', 'Customer', 'ServiceItem', 'HourlyRate', 'Taxable', 'Class', 'Location'];

export function buildQboTimesheetCsv(shifts, { qbNames = {}, classes = {} } = {}) {
  const rows = [QBO_TIME_HEADER];
  let skipped = 0;
  for (const s of shifts) {
    if (s.open || !s.minutes) { skipped += 1; continue; }
    const cls = classes[s.locationId] || s.store;
    rows.push([mdy(s.date), payrollName(s.employee, qbNames), s.start.slice(11, 16), s.end.slice(11, 16),
      s.flag ? `${s.store} — ${s.flag}` : s.store, 'N', '', '', '', '', '', cls, cls]);
  }
  return { text: csvText(rows), count: rows.length - 1, skipped };
}

// ── ADP pay data import ──────────────────────────────────────────────────────
// One row per employee per pay period; File # is ADP's employee id and must
// be mapped in the profile. Rows without one are reported, not guessed.
export const ADP_HEADER = ['Co Code', 'Batch ID', 'File #', 'Reg Hours', 'O/T Hours'];

export function buildAdpCsv(employees, { coCode = '', batchId = '', fileNumbers = {}, overtimeWeeklyHours = null } = {}) {
  const rows = [ADP_HEADER];
  const missing = [];
  for (const { employee: e } of employees) {
    const file = fileNumbers[e.employeeId];
    if (!file) { missing.push(e); continue; }
    const { reg, ot } = splitOvertime(e.shifts, overtimeWeeklyHours);
    rows.push([coCode, batchId, file, decimalHours(reg).toFixed(2), decimalHours(ot).toFixed(2)]);
  }
  return { text: csvText(rows), count: rows.length - 1, missing };
}
